import {
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useMediaQuery } from 'react-responsive'

import classNames from 'classnames'

import styles from './Stepper.module.scss'

import RecordCard from '../RecordCard/RecordCard'
import { steps } from './config'

const FILTERS = [
  {
    id: 'all',
    title: 'Все',
    tag: null,
  },
  {
    id: 'city',
    title: 'Города',
    tag: 'Город',
  },
  {
    id: 'buildings',
    title: 'Здания',
    tag: 'Здание',
  },
  {
    id: 'places',
    title: 'Места',
    tag: 'Место',
  },
  {
    id: 'streets',
    title: 'Улицы',
    tag: 'Улица',
  },
  {
    id: 'transport',
    title: 'Транспорт',
    tag: 'Транспорт',
  },
  {
    id: 'nature',
    title: 'Природа',
    tag: 'Природа',
  },
  {
    id: 'sculptures',
    title: 'Скульптуры',
    tag: 'Скульптура',
  },
] as const

type FilterId = (typeof FILTERS)[number]['id']

type RecordsNavigationContainerProps = {
  children: ReactNode
  isCompact: boolean
}

type MapLinesElement = HTMLElement & {
  mapLinesInstance?: {
    keepActiveItemVisible: () => void
  }
}

const RecordsNavigationContainer = ({
  children,
  isCompact,
}: RecordsNavigationContainerProps) => {
  if (isCompact) {
    return <>{children}</>
  }

  return <div className="maps-navs records-navigation">{children}</div>
}

const Stepper = () => {
  const isCompactNavigation = useMediaQuery({ maxWidth: 1025 })
  const [activeFilter, setActiveFilter] = useState<FilterId>('all')
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isRecordsHeaderVisible, setIsRecordsHeaderVisible] = useState(false)
  const [isNavigationHidden, setIsNavigationHidden] = useState(false)
  const navigationHideSuppressedUntil = useRef(0)
  const compactFiltersScrollX = useRef<number | null>(null)

  const records = useMemo(
    () =>
      steps.map((record, originalIndex) => ({
        originalIndex,
        record,
      })),
    [],
  )

  const filters = useMemo(
    () =>
      FILTERS.map(filter => ({
        ...filter,
        count:
          filter.tag === null
            ? records.length
            : records.filter(({ record }) => record.tag === filter.tag).length,
      })),
    [records],
  )

  const filteredRecords = useMemo(() => {
    const selectedFilter = FILTERS.find(filter => filter.id === activeFilter)

    if (!selectedFilter?.tag) {
      return records
    }

    return records.filter(({ record }) => record.tag === selectedFilter.tag)
  }, [activeFilter, records])

  const scrollToFirstRecord = useCallback(() => {
    const [firstRecord] = filteredRecords
    const firstSection = firstRecord
      ? document.getElementById(`section${firstRecord.originalIndex}`)
      : null

    if (!firstSection) {
      return
    }

    setIsMenuOpen(false)

    firstSection.scrollIntoView({
      behavior: 'instant',
      block: 'start',
    })
  }, [filteredRecords])

  const handleFilterChange = useCallback(
    (filterId: FilterId) => {
      if (filterId === activeFilter) {
        if (isCompactNavigation) {
          scrollToFirstRecord()
        }

        return
      }

      if (isCompactNavigation) {
        navigationHideSuppressedUntil.current = performance.now() + 500
        setIsNavigationHidden(false)

        const filtersContainer = document.querySelector<HTMLElement>(
          '.records-navigation__filters',
        )

        compactFiltersScrollX.current = filtersContainer?.scrollLeft ?? 0
      }

      setActiveFilter(filterId)
    },
    [activeFilter, isCompactNavigation, scrollToFirstRecord],
  )

  useEffect(() => {
    let frameId: number | null = null

    const updateRecordsHeaderVisibility = () => {
      if (frameId !== null) {
        return
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null

        const recordsSection = document.getElementById('records')
        const isVisible = Boolean(
          recordsSection && recordsSection.getBoundingClientRect().top <= 0,
        )

        setIsRecordsHeaderVisible(isVisible)
      })
    }

    updateRecordsHeaderVisibility()
    window.addEventListener('scroll', updateRecordsHeaderVisibility, {
      passive: true,
    })
    window.addEventListener('resize', updateRecordsHeaderVisibility)

    return () => {
      window.removeEventListener('scroll', updateRecordsHeaderVisibility)
      window.removeEventListener('resize', updateRecordsHeaderVisibility)

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [])

  useLayoutEffect(() => {
    navigationHideSuppressedUntil.current = performance.now() + 500
    setIsNavigationHidden(false)
  }, [activeFilter])

  useEffect(() => {
    if (!isCompactNavigation) {
      setIsNavigationHidden(false)
      return
    }

    let frameId: number | null = null

    const updateNavigationVisibility = () => {
      if (frameId !== null) {
        return
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = null

        if (performance.now() < navigationHideSuppressedUntil.current) {
          setIsNavigationHidden(false)
          return
        }

        const lastRecordMap = document.querySelector<HTMLElement>(
          '.records-section__list > .records-section__card:last-child .record-card__map-container',
        )

        if (!lastRecordMap) {
          setIsNavigationHidden(false)
          return
        }

        const headerOffset = (50 / 325) * window.innerWidth

        setIsNavigationHidden(
          lastRecordMap.getBoundingClientRect().bottom < headerOffset,
        )
      })
    }

    updateNavigationVisibility()
    window.addEventListener('scroll', updateNavigationVisibility, {
      passive: true,
    })
    window.addEventListener('resize', updateNavigationVisibility)

    return () => {
      window.removeEventListener('scroll', updateNavigationVisibility)
      window.removeEventListener('resize', updateNavigationVisibility)

      if (frameId !== null) {
        window.cancelAnimationFrame(frameId)
      }
    }
  }, [activeFilter, isCompactNavigation])

  useLayoutEffect(() => {
    const filtersScrollX = compactFiltersScrollX.current

    if (filtersScrollX === null) {
      return
    }

    const filtersContainer = document.querySelector<HTMLElement>(
      '.records-navigation__filters',
    )

    if (filtersContainer) {
      filtersContainer.scrollLeft = filtersScrollX
    }

    compactFiltersScrollX.current = null
  }, [activeFilter])

  useLayoutEffect(() => {
    if (!isCompactNavigation || !isMenuOpen) {
      return
    }

    let frameId = 0

    const scrollActiveItemIntoPosition = () => {
      frameId = window.requestAnimationFrame(() => {
        document
          .querySelector<MapLinesElement>('.records-navigation__lines')
          ?.mapLinesInstance?.keepActiveItemVisible()
      })
    }

    scrollActiveItemIntoPosition()

    const transitionTimer = window.setTimeout(scrollActiveItemIntoPosition, 450)

    return () => {
      window.clearTimeout(transitionTimer)
      window.cancelAnimationFrame(frameId)
    }
  }, [activeFilter, isCompactNavigation, isMenuOpen])

  const previousFilter = useRef(activeFilter)

  useEffect(() => {
    if (previousFilter.current === activeFilter) {
      return
    }

    previousFilter.current = activeFilter

    scrollToFirstRecord()
  }, [activeFilter, scrollToFirstRecord])

  return (
    <section className="maps records-section" id="records">
      <div
        className={classNames(
          styles.wrapper,
          'maps-wrapper',
          'records-section__wrapper',
        )}
      >
        <RecordsNavigationContainer isCompact={isCompactNavigation}>
          <div
            className={classNames(
              'maps-navs-btns',
              'records-navigation__header',
              {
                'header-visible-burger': isRecordsHeaderVisible,
                'hidden-top': isNavigationHidden,
                'list-visible': isMenuOpen,
              },
            )}
          >
            <div className="maps-navs-btns-wrapper records-navigation__filters">
              {filters.map(filter => {
                const isActive = activeFilter === filter.id

                return (
                  <button
                    key={filter.id}
                    type="button"
                    className={classNames(
                      'maps-navs-btn',
                      'records-navigation__filter',
                      {
                        active: isActive,
                        'records-navigation__filter--active': isActive,
                      },
                    )}
                    aria-pressed={isActive}
                    onClick={() => handleFilterChange(filter.id)}
                  >
                    <span className="maps-navs-btn__title records-navigation__filter-title">
                      {filter.title}
                    </span>

                    <strong className="maps-navs-btn__count records-navigation__filter-count">
                      {filter.count}
                    </strong>
                  </button>
                )
              })}
            </div>

            <button
              type="button"
              className={classNames(
                'maps-navs__burger',
                'tablet-true',
                'records-navigation__burger',
                {
                  active: isMenuOpen,
                  hidden: !isRecordsHeaderVisible,
                  'records-navigation__burger--active': isMenuOpen,
                },
              )}
              aria-label={
                isMenuOpen
                  ? 'Закрыть список рекордов'
                  : 'Открыть список рекордов'
              }
              aria-expanded={isMenuOpen}
              onClick={() => {
                setIsMenuOpen(value => !value)
              }}
            >
              <span className="maps-navs__burger-inner records-navigation__burger-inner">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="burger-icon records-navigation__burger-icon"
                  aria-hidden="true"
                >
                  <path
                    d="M6.83008 5L17.17 5"
                    stroke="#1A1A1A"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M6.83008 10L17.17 10"
                    stroke="#1A1A1A"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M6.83008 15L17.17 15"
                    stroke="#1A1A1A"
                    strokeWidth="1.66667"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M2.99707 5H3.00457"
                    stroke="#1A1A1A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M2.99707 10H3.00457"
                    stroke="#1A1A1A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />

                  <path
                    d="M2.99707 15H3.00457"
                    stroke="#1A1A1A"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>

                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                  fill="none"
                  className="close-icon records-navigation__close-icon"
                  aria-hidden="true"
                >
                  <path
                    d="M14.5725 4.24419C14.8977 3.91877 15.4256 3.9191 15.7512 4.24419C16.0764 4.56954 16.0764 5.09648 15.7512 5.42192L11.1799 9.99516L15.7561 14.5743C16.0813 14.8997 16.0814 15.4267 15.7561 15.752C15.4305 16.0774 14.9027 16.0775 14.5773 15.752L10.0022 11.1739L5.42207 15.7579C5.09679 16.0833 4.5689 16.083 4.24336 15.7579C3.91816 15.4326 3.91732 14.9046 4.24239 14.5791L8.82344 9.99516L4.25215 5.42192C3.92729 5.09637 3.92779 4.56841 4.25313 4.24321C4.57858 3.91842 5.10564 3.91912 5.43086 4.24419L10.0012 8.81645L14.5725 4.24419Z"
                    fill="#1A1A1A"
                  />
                </svg>
              </span>
            </button>
          </div>

          <div
            className={classNames(
              'maps-navs-hidden',
              'records-navigation__body',
              {
                active: isMenuOpen,
                'records-navigation__body--active': isMenuOpen,
              },
            )}
          >
            <div className="map-lines-wrapper records-navigation__lines-wrapper">
              <div
                key={activeFilter}
                className="map-lines records-navigation__lines"
              >
                <ol className="map-lines__list records-navigation__list">
                  {filteredRecords.map(({ originalIndex, record }) => (
                    <li
                      key={`${record.name}-${originalIndex}`}
                      className="map-lines__item records-navigation__item"
                      data-record-index={originalIndex}
                    >
                      <button
                        type="button"
                        className="map-lines__button records-navigation__item-button"
                        data-section={`section${originalIndex}`}
                        onClick={() => setIsMenuOpen(false)}
                      >
                        <span className="map-lines__marker records-navigation__marker">
                          <span className="map-lines__arrow records-navigation__arrow" />
                        </span>

                        <span className="map-lines__title records-navigation__item-title">
                          {record.name}
                        </span>
                      </button>
                    </li>
                  ))}
                </ol>

                {activeFilter !== 'all' && (
                  <p className="records-navigation__end-message">
                    Это все рекорды категории,
                    <br />
                    но есть ещё{' '}
                    <button
                      type="button"
                      className="records-navigation__end-action"
                      onClick={() => handleFilterChange('all')}
                    >
                      много других
                    </button>
                  </p>
                )}
              </div>
            </div>

            <a
              href="https://2gis.onelink.me/nw2S/q7h8k5s4"
              target="_blank"
              rel="noopener noreferrer"
              className="maps-navs__show records-navigation__show-button"
            >
              <span className="maps-navs__show-text records-navigation__show-button-text">
                Посмотреть рекорды на карте
              </span>
            </a>
          </div>
        </RecordsNavigationContainer>

        <div
          className={classNames(
            styles.wrapper_maps,
            'maps-list',
            'records-section__list',
          )}
        >
          {filteredRecords.map(
            ({
              originalIndex,
              record: {
                map,
                viewportBounds,
                fitPolylineToView,
                selectObjects,
                markerCords,
                polygon,
                polygons,
                polyline,
                title,
                description,
                address,
                href,
                polylineWithoutMarksSE,
                tag,
                description_mob,
              },
            }) => (
              <RecordCard
                key={originalIndex}
                className="records-section__card"
                index={originalIndex}
                map={map}
                viewportBounds={viewportBounds}
                fitPolylineToView={fitPolylineToView}
                href={href}
                tag={tag}
                title={title}
                address={address}
                polylineWithoutMarksSE={polylineWithoutMarksSE}
                markerCords={markerCords}
                description={description}
                mobileDescription={description_mob}
                setSelectedObjects={selectObjects}
                polygon={polygon}
                polygons={polygons}
                polyline={polyline}
              />
            ),
          )}
        </div>
      </div>
    </section>
  )
}

export default Stepper
